import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import authService from '../services/authService'
import { useTranslation } from '../hooks/use-i18n'

interface ForgotPasswordForm {
  email: string
}

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [resetLink, setResetLink] = useState<string | null>(null)
  const { register, handleSubmit, formState: { errors } } = useForm<ForgotPasswordForm>()

  const onSubmit = async (data: ForgotPasswordForm) => {
    try {
      setIsLoading(true)
      setResetLink(null)
      const result = await authService.forgotPassword(data.email.trim().toLowerCase())
      setSubmitted(true)
      if (result.resetLink) setResetLink(result.resetLink)
      toast.success(
        result.resetLink
          ? t('toast.forgotPasswordDev')
          : t('toast.forgotPasswordEmail')
      )
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('toast.forgotPasswordError'))
    } finally {
      setIsLoading(false)
    }
  }

  const copyLink = () => {
    if (resetLink) {
      navigator.clipboard.writeText(resetLink)
      toast.success(t('toast.linkCopied'))
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center pt-8 pb-12 lg:pt-12 lg:pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md lg:max-w-lg xl:max-w-xl w-full space-y-8">
        <div className="blue-sky-card rounded-3xl p-8 lg:p-10 xl:p-12 shadow-2xl">
          <div className="text-center mb-8 lg:mb-10 xl:mb-12">
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-2 lg:mb-3">
              {t('auth.forgotPassword.title')}
            </h2>
            <p className="text-white/80 text-base lg:text-lg xl:text-xl">
              {t('auth.forgotPassword.subtitle')}
            </p>
          </div>

          {submitted ? (
            <div className="space-y-6">
              <p className="text-white/90 text-center text-sm lg:text-base">
                {resetLink
                  ? t('auth.forgotPassword.successDev')
                  : t('auth.forgotPassword.successEmail')}
              </p>
              {resetLink && (
                <div className="space-y-3">
                  <a
                    href={resetLink}
                    className="block text-sm text-sky-400 hover:text-sky-300 hover:underline break-all"
                  >
                    {resetLink}
                  </a>
                  <button
                    onClick={copyLink}
                    className="w-full bg-white/10 border border-white/20 hover:bg-white/20 text-white py-2.5 px-4 rounded-xl font-medium text-sm transition-all duration-300"
                  >
                    {t('auth.forgotPassword.copyLink')}
                  </button>
                </div>
              )}
              <button
                onClick={() => { setSubmitted(false); setResetLink(null) }}
                className="w-full bg-white/10 border border-white/20 hover:bg-white/20 text-white py-3 lg:py-4 px-4 lg:px-6 rounded-xl font-semibold text-base lg:text-lg transition-all duration-300"
              >
                {t('auth.forgotPassword.sendAnother')}
              </button>
            </div>
          ) : (
            <form className="space-y-6 lg:space-y-8" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label htmlFor="email" className="block text-sm lg:text-base font-medium text-white/90 mb-2 lg:mb-3">
                  {t('auth.forgotPassword.emailLabel')}
                </label>
                <input
                  {...register('email', {
                    required: t('auth.validation.emailRequired'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('auth.validation.invalidEmail'),
                    },
                  })}
                  type="email"
                  className="w-full px-4 lg:px-5 xl:px-6 py-3 lg:py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-300 text-sm lg:text-base"
                  placeholder={t('auth.forgotPassword.emailPlaceholder')}
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-400">{errors.email.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white py-3 lg:py-4 px-4 lg:px-6 rounded-xl font-semibold text-base lg:text-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl"
              >
                {isLoading ? t('auth.forgotPassword.submitting') : t('auth.forgotPassword.submit')}
              </button>
            </form>
          )}

          <div className="mt-6 lg:mt-8 text-center">
            <Link
              to="/login"
              className="text-sm lg:text-base text-white/80 hover:text-white transition-colors hover:underline"
            >
              {t('auth.forgotPassword.backToSignIn')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPassword
